// src/pages/Tools/ModifyColumns.jsx
import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { FaSearch, FaGripLines } from "react-icons/fa";
import { RxCrossCircled } from "react-icons/rx";
import axios from "axios";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Link, useNavigate } from "react-router-dom";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdLockOutline } from "react-icons/md";
import { Pencil } from "lucide-react";

// Variable to toggle the display of column types, easily accessible for developers
const showType = false;

// Helper function to format raw type strings into user-friendly labels
const formatColumnType = (type) => {
  if (!type) return "";
  switch (type.toLowerCase()) {
    case "number":
      return "Number";
    case "currency":
      return "Currency";
    case "float":
      return "Percentage";
    case "float-no-perc":
      return "Decimal";
    case "boolean":
      return "Boolean";
    case "string":
      return "Text";
    default:
      // Capitalize first letter and replace dashes with spaces for any unmapped types
      return type
        .replace(/[-_]/g, " ")
        .replace(
          /\w\S*/g,
          (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase(),
        );
  }
};

function ModifyColumns({
  columns,
  setColumns,
  setShowColumnsMenu,
  setTableVisible,
  setTotal,
  setTotalShow,
  segmentType,
  selectedGroup,
  selectedCampaignTypes,
  isPerformancePage = false,
  colsLimit,
}) {
  const navigate = useNavigate();
  const [tempColumns, setTempColumns] = useState(() =>
    JSON.parse(JSON.stringify(columns)),
  );

  const [columnSearchString, setColumnSearchString] = useState("");
  const [expandedCategories, setExpandedCategories] = useState([]);

  // Calculate Categories
  const uniqueCategories = Array.from(
    new Set(tempColumns.map((col) => col.category)),
  );
  const categoriesToMoveDown = ["Custom Columns", "Budget Name"];
  const orderedCategories = [
    ...uniqueCategories.filter((cat) => !categoriesToMoveDown.includes(cat)),
    ...uniqueCategories.filter((cat) => categoriesToMoveDown.includes(cat)),
  ];

  // Open first category by default on mount
  useEffect(() => {
    if (orderedCategories.length > 0 && expandedCategories.length === 0) {
      setExpandedCategories([orderedCategories[0]]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lock body scroll when drawer is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  // Make sure we only run if document exists (NextJS/SSR check)
  if (typeof document === "undefined") return null;

  const toggleCategory = (category) => {
    setExpandedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category],
    );
  };

  const deleteCustomColumn = (id, name) => {
    let con = window.confirm(`Delete ${name} Column?`);

    if (con) {
      let url = isPerformancePage
        ? "https://api.confidanto.com/custom-columns-project-performance/delete"
        : "https://api.confidanto.com/custom_columns/delete-custom-column";
      axios
        .post(url, {
          email: localStorage.getItem("email"),
          id: id,
        })
        .then((res) => {
          let newColumns = tempColumns.filter((col) => col.id !== id);
          setTempColumns(newColumns);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  const visibleColumnsCount = tempColumns.filter((col) => col.visible).length;

  const toggleColumnVisibility = (key) => {
    const targetCol = tempColumns.find((col) => col.key === key);
    if (!targetCol || targetCol.isLocked) return;

    // Disallow selecting more if colsLimit is specified and reached
    if (!targetCol.visible && colsLimit && visibleColumnsCount >= colsLimit) {
      return;
    }

    setTempColumns(
      tempColumns.map((col) =>
        col.key === key ? { ...col, visible: !col.visible } : col,
      ),
    );
  };

  // Select/Deselect All Logic
  const unlockedColumns = tempColumns.filter((col) => !col.isLocked);
  const allSelected =
    unlockedColumns.length > 0 && unlockedColumns.every((col) => col.visible);

  const toggleSelectAll = () => {
    if (allSelected) {
      setTempColumns(
        tempColumns.map((col) =>
          col.isLocked ? col : { ...col, visible: false },
        ),
      );
    } else {
      let count = tempColumns.filter(
        (col) => col.isLocked && col.visible,
      ).length;
      setTempColumns(
        tempColumns.map((col) => {
          if (col.isLocked) return col;
          if (colsLimit) {
            if (count < colsLimit) {
              count++;
              return { ...col, visible: true };
            }
            return { ...col, visible: false };
          }
          return { ...col, visible: true };
        }),
      );
    }
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    if (tempColumns[source.index].isLocked || destination.index === 0) return;

    const visibleColumns = tempColumns.filter((col) => col.visible);

    const reorderedVisibleColumns = Array.from(visibleColumns);
    const [movedVisibleItem] = reorderedVisibleColumns.splice(source.index, 1);
    reorderedVisibleColumns.splice(destination.index, 0, movedVisibleItem);

    const updatedFullColumns = [];
    let visibleItemIndex = 0;

    tempColumns.forEach((originalColumn) => {
      if (originalColumn.visible) {
        if (visibleItemIndex < reorderedVisibleColumns.length) {
          updatedFullColumns.push(reorderedVisibleColumns[visibleItemIndex]);
          visibleItemIndex++;
        } else {
          updatedFullColumns.push(originalColumn);
        }
      } else {
        updatedFullColumns.push(originalColumn);
      }
    });

    setTempColumns(updatedFullColumns);
  };

  const applyChanges = () => {
    setColumns(tempColumns);

    if (setTotal !== undefined && setTotalShow !== undefined) {
      setTotal({});
      setTotalShow(false);
      setTimeout(() => setTotalShow(true), 0);
    }

    setShowColumnsMenu(false);
    setTableVisible(true);
  };

  const cancelChanges = () => {
    setShowColumnsMenu(false);
    setTableVisible(true);
  };

  // Stop events from bubbling up through React to the parent component
  const stopPropagation = (e) => {
    e.stopPropagation();
    // Use native propagation stop just in case parent relies on document.addEventListener
    if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
      e.nativeEvent.stopImmediatePropagation();
    }
  };

  // The Column Item for the Left Panel
  const ColumnItem = ({ column }) => {
    const isChecked = column.visible;
    const isLimitReached = Boolean(
      colsLimit && visibleColumnsCount >= colsLimit && !isChecked,
    );
    const isDisabled = column.isLocked || isLimitReached;

    return (
      <div
        className={`flex items-center justify-between p-2 rounded transition-colors group ${
          isDisabled
            ? "opacity-60 cursor-not-allowed"
            : "cursor-pointer hover:bg-blue-50"
        }`}
        title={
          isLimitReached ? `Limit of ${colsLimit} columns reached` : undefined
        }
        onClick={() => {
          if (!isDisabled) toggleColumnVisibility(column.key);
        }}
      >
        <label
          className="flex items-center space-x-3 w-full cursor-inherit"
          onClick={(e) => e.preventDefault()} // Let parent div handle the click
        >
          <input
            type="checkbox"
            checked={isChecked}
            disabled={isDisabled}
            readOnly
            className={`w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 pointer-events-none ${
              isDisabled ? "cursor-not-allowed" : "cursor-pointer"
            }`}
          />
          <div className="flex flex-col justify-center">
            <span className="text-sm select-none text-gray-700 group-hover:text-gray-900 leading-tight">
              {column.title}
            </span>
            {showType && column.type && (
              <span className="text-[10px] text-gray-400 mt-0.5 select-none">
                {formatColumnType(column.type)}
              </span>
            )}
          </div>
        </label>

        {column.category === "Custom Columns" && (
          <div className="flex flex-row space-x-2 shrink-0">
            <button
              onClick={(e) => {
                e.stopPropagation();
                const editPath = isPerformancePage
                  ? `/performance-custom-column?id=${column.id}`
                  : `/custom-column?id=${column.id}`;
                navigate(editPath);
              }}
              className="text-gray-400 hover:text-blue-600 p-1"
            >
              <Pencil size={15} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                deleteCustomColumn(column.id, column.title);
              }}
              className="text-gray-400 hover:text-red-600 p-1"
            >
              <RiDeleteBin6Line size={15} />
            </button>
          </div>
        )}
      </div>
    );
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[99999] flex justify-end"
      // CRITICAL FIX: prevents parent click-outside hooks from automatically hiding the modal
      onClick={stopPropagation}
      onMouseDown={stopPropagation}
    >
      {/* Blacken background overlay */}
      <div
        className="absolute inset-0 bg-black/60 transition-opacity cursor-pointer"
        onClick={cancelChanges}
      ></div>

      {/* Drawer Panel */}
      <div className="relative w-full md:w-[75vw] lg:w-[65vw] max-w-5xl h-full bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out animate-in slide-in-from-right">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0 bg-white">
          <div className="flex items-center">
            <button
              onClick={cancelChanges}
              className="mr-4 text-gray-500 hover:text-gray-800 transition-colors"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
            <h2 className="text-xl font-normal text-gray-800">
              Modify columns
            </h2>
          </div>
          <Link
            to={
              isPerformancePage
                ? "/performance-custom-column"
                : "/custom-column"
            }
            className="flex items-center space-x-1 rounded-md text-blue-600 px-3 py-1.5 text-sm font-medium hover:bg-blue-50 transition-colors"
          >
            <span>+ Custom Column</span>
          </Link>
        </div>

        {/* Modal Body split pane */}
        <div className="flex flex-1 overflow-hidden bg-white">
          {/* Left Settings Pane (Search & Categories) */}
          <div className="flex-1 flex flex-col border-r border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 shrink-0 flex items-center space-x-3">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Search columns..."
                  className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-300 rounded-lg outline-none text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all"
                  value={columnSearchString}
                  onChange={(e) =>
                    setColumnSearchString(e.target.value.toLowerCase())
                  }
                />
                <FaSearch className="absolute left-3.5 top-3 text-gray-400" />
              </div>
              {false && (
                <button
                  onClick={toggleSelectAll}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors whitespace-nowrap outline-none border border-transparent focus:ring-2 focus:ring-blue-100"
                >
                  {allSelected ? "Deselect All" : "Select All"}
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto">
              {orderedCategories.map((category) => {
                const categoryCols = tempColumns.filter(
                  (col) => col.category === category,
                );
                const filteredCols = categoryCols.filter((col) =>
                  col.title.toLowerCase().includes(columnSearchString),
                );

                if (columnSearchString && filteredCols.length === 0)
                  return null;
                const isExpanded =
                  expandedCategories.includes(category) ||
                  columnSearchString !== "";

                return (
                  <div key={category} className="border-b border-gray-100">
                    <button
                      onClick={() => toggleCategory(category)}
                      className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 focus:outline-none transition-colors"
                    >
                      <span className="font-medium text-gray-700 text-sm">
                        {category}
                      </span>
                      <svg
                        className={`text-gray-400 transform transition-transform duration-200 ${
                          isExpanded ? "rotate-180" : ""
                        }`}
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </button>

                    {isExpanded && (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-2 gap-x-4 px-6 pb-5 pt-1">
                        {filteredCols.map((col) => (
                          <ColumnItem key={col.key} column={col} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Selection Summary Pane (Drag & Drop) */}
          <div className="w-[35%] min-w-[300px] shrink-0 flex flex-col bg-gray-50/50">
            <div className="px-6 py-4 border-b border-gray-200 shrink-0">
              <h3 className="font-semibold text-gray-800 text-sm">
                Your columns ({visibleColumnsCount}
                {colsLimit !== undefined && ` / ${colsLimit}`})
              </h3>
              <p className="text-[11px] text-gray-500 mt-0.5">
                {colsLimit && visibleColumnsCount >= colsLimit
                  ? `Limit of ${colsLimit} columns reached`
                  : "Drag and drop to reorder"}
              </p>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-3">
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="columnsList">
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="space-y-2"
                    >
                      {tempColumns
                        .filter((column) => column.visible)
                        .map((column, index) => (
                          <Draggable
                            key={column.id}
                            draggableId={column.id.toString()}
                            index={index}
                            isDragDisabled={column.isLocked}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={`flex items-center justify-between px-3 py-2 bg-white border ${
                                  snapshot.isDragging
                                    ? "border-blue-400 shadow-md"
                                    : "border-gray-200 shadow-sm"
                                } rounded-md group hover:border-blue-300 transition-colors`}
                              >
                                <div className="flex items-center space-x-3 overflow-hidden">
                                  {!column.isLocked ? (
                                    <FaGripLines className="text-gray-400 cursor-grab shrink-0 hover:text-gray-600" />
                                  ) : (
                                    <MdLockOutline className="text-gray-400 shrink-0" />
                                  )}
                                  <div className="flex flex-col justify-center overflow-hidden">
                                    <span className="text-sm text-gray-700 truncate leading-tight">
                                      {column.title}
                                    </span>
                                    {showType && column.type && (
                                      <span className="text-[10px] text-gray-400 mt-0.5 truncate">
                                        {formatColumnType(column.type)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {!column.isLocked && (
                                  <button
                                    onClick={() =>
                                      toggleColumnVisibility(column.key)
                                    }
                                    className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-1.5 rounded transition-colors"
                                    title="Hide column"
                                  >
                                    <RxCrossCircled size={16} />
                                  </button>
                                )}
                              </div>
                            )}
                          </Draggable>
                        ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center space-x-3 bg-white shrink-0">
          <button
            onClick={applyChanges}
            className="px-6 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
          >
            Apply
          </button>
          <button
            onClick={cancelChanges}
            className="px-6 py-2.5 text-blue-600 bg-transparent text-sm font-medium rounded-lg hover:bg-blue-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default ModifyColumns;
